/**
 * Dark-terminal horizontal-bar list — the salience analog of the light-shell
 * FacetBars. Renders a labelled value distribution (geo snapshot, stance split,
 * frame split) on the dark panels. Optional per-key colour map; otherwise a
 * single accent.
 */
interface TerminalBarsProps {
  /** key → numeric value (count or share fraction). */
  counts: Record<string, number>;
  labelMap?: Record<string, string>;
  colourMap?: Record<string, string>;
  accent?: string;
  /** Format a value for the right-hand column (default: integer). */
  format?: (v: number) => string;
  emptyMessage?: string;
}

export function TerminalBars({
  counts,
  labelMap,
  colourMap,
  accent = "#4f8cff",
  format = (v) => v.toLocaleString("en-GB"),
  emptyMessage = "No data.",
}: TerminalBarsProps) {
  const rows = Object.entries(counts)
    .filter(([k]) => k !== "")
    .sort((a, b) => b[1] - a[1]);
  if (rows.length === 0) {
    return <div className="text-sm text-terminal-ink-muted">{emptyMessage}</div>;
  }
  const max = rows[0][1] || 1;

  return (
    <div className="space-y-1.5">
      {rows.map(([key, value]) => {
        const width = max > 0 ? (100 * value) / max : 0;
        const colour = colourMap?.[key] ?? accent;
        return (
          <div
            key={key}
            className="grid grid-cols-[minmax(0,90px)_1fr_auto] items-center gap-2.5 text-sm"
          >
            <span
              className="truncate text-terminal-ink-muted"
              title={labelMap?.[key] ?? key}
            >
              {labelMap?.[key] ?? key}
            </span>
            <span className="h-2 overflow-hidden rounded-full bg-white/8">
              <span
                className="block h-full rounded-full"
                style={{ width: `${width}%`, background: colour }}
              />
            </span>
            <span className="font-mono text-xs tabular-nums text-terminal-ink">
              {format(value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export const STANCE_LABELS: Record<string, string> = {
  supportive: "Supportive",
  opposed: "Opposed",
  neutral: "Neutral",
  mixed: "Mixed",
};

export const STANCE_COLOURS: Record<string, string> = {
  supportive: "#16c784",
  opposed: "#ea3943",
  neutral: "#9aa0d8",
  mixed: "#f5a524",
};

export const FRAME_LABELS: Record<string, string> = {
  fairness: "Fairness",
  capital_flight: "Capital flight",
  feasibility: "Feasibility",
  billionaires: "Billionaires",
};
